import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BuilderJS6 from './BuilderJS6';
import { toast } from 'react-toastify';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';

const BuilderJS6Page = ({ viewMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState(null);
  const [themeUrl, setThemeUrl] = useState('/themes/standard/Y1_c_1-2-1_Column_Layout_theme');
  const [isLoading, setIsLoading] = useState(!!id);

  useEffect(() => {
    if (id) {
      loadTemplate(id);
    }
  }, [id]);

  const loadTemplate = async (templateId) => {
    try {
      setIsLoading(true);
      
      if (!templateId) {
        throw new Error('Template ID is required');
      }
      
      console.log('Loading template with ID:', templateId);
      let response;
      try {
        response = await AxiosInstance.get(`/templates/by-template-id/${templateId}`);
      } catch (error) {
        // Axios throws error for non-2xx responses, but response might still exist
        if (error.response) {
          response = error.response;
        } else {
          console.error('Network error:', error);
          throw new Error('Network error: Could not connect to server');
        }
      }
      
      // Check if response exists
      if (!response) {
        console.error('No response object received');
        throw new Error('No response received from server fffffff');
      }
      
      // Check if response.data exists
      if (!response.data) {
        console.error('Response structure:', response);
        throw new Error('Invalid response structure from server');
      }
      
      console.log('Template response received:', response.data);
      
      // Check for error status codes
      if (response.data.statusCode && response.data.statusCode !== 200) {
        const errorMsg = response.data.message || 'Failed to load template';
        throw new Error(errorMsg);
      }
      
      // Check if template data exists
      if (!response.data.data) {
        console.warn('Response data:', response.data);
        // If data is empty object, create empty template structure
        if (response.data.data === null || (typeof response.data.data === 'object' && Object.keys(response.data.data).length === 0)) {
          console.warn('Template data is empty, creating empty template structure');
          const emptyData = {
            elementLists: [],
            formats: {},
            theme: 'Blank'
          };
          setInitialData(emptyData);
          setThemeUrl('/themes/standard/Y0_Blank');
          setIsLoading(false);
          return;
        }
        throw new Error('Template data not found in response');
      }
      
      // Get JSON data
      let jsonData = response.data.data.json || response.data.data.content;
      
      // If no JSON data, create empty structure
      if (!jsonData) {
        console.warn('No JSON data found in template, creating empty template');
        jsonData = {
          elementLists: [],
          formats: {},
          theme: 'Blank'
        };
      }
      
      // Parse if string
      if (typeof jsonData === 'string') {
        try {
          jsonData = JSON.parse(jsonData);
        } catch (e) {
          console.error('Error parsing JSON:', e);
          // Create empty structure if parsing fails
          jsonData = {
            elementLists: [],
            formats: {},
            theme: 'Blank'
          };
        }
      }
      
      // Ensure jsonData is an object
      if (!jsonData || typeof jsonData !== 'object') {
        jsonData = {
          elementLists: [],
          formats: {},
          theme: 'Blank'
        };
      }
      
      // Ensure required properties exist
      if (!jsonData.elementLists) {
        jsonData.elementLists = [];
      }
      if (!jsonData.formats) {
        jsonData.formats = {};
      }
      
      if (jsonData && jsonData.theme) {
          // Map theme names to theme paths
          const themeMap = {
            'Blank': '/themes/standard/Y0_Blank',
            '1 Column Layout': '/themes/standard/Y1_a_1_Column_Layout',
            '1-2 Column Layout': '/themes/standard/Y1_b_1-2-Column_Layout',
            '1-2-1 Column Layout': '/themes/standard/Y1_c_1-2-1_Column_Layout_theme',
            '1-3 Column Layout': '/themes/standard/Y1_d_1-3_Column_Layout_theme',
            '1-3-1 Column Layout': '/themes/standard/Y1_e_1-3-1_Column_Layout_theme',
            '1-3-2 Column Layout': '/themes/standard/Y1_f_1-3-2_Column_Layout_theme',
            '2 Column Layout': '/themes/standard/Y2_a_2_Column_Layout_theme',
            '2-1 Column Layout': '/themes/standard/Y2_b_2-1_Column_Layout_theme',
            '2-1-2 Column Layout': '/themes/standard/Y2_c_2-1-2_Column_Layout_theme',
            '3 Column Layout': '/themes/standard/Y3_a_3_Column_Layout_theme',
            '3-1-3 Column Layout': '/themes/standard/Y3_b_3-1-3_Column_Layout_theme',
            'Pricing Table': '/themes/standard/Y4_Pricing_Table_theme',
            'Listing Tables': '/themes/standard/Y4_Listing_Tables_theme',
            'Forms Building': '/themes/standard/Y4_Forms_Building_theme',
            // Also handle internal theme names
            '1_3_Column_Layout': '/themes/standard/Y1_d_1-3_Column_Layout_theme',
            '1_3_1_Column_Layout': '/themes/standard/Y1_e_1-3-1_Column_Layout_theme',
            '1_2_1_Column_Layout': '/themes/standard/Y1_c_1-2-1_Column_Layout_theme',
            '1_2_Column_Layout': '/themes/standard/Y1_b_1-2-Column_Layout',
            '1_Column_Layout': '/themes/standard/Y1_a_1_Column_Layout',
            '2_Column_Layout': '/themes/standard/Y2_a_2_Column_Layout_theme',
            '2_1_Column_Layout': '/themes/standard/Y2_b_2-1_Column_Layout_theme',
            '2_1_2_Column_Layout': '/themes/standard/Y2_c_2-1-2_Column_Layout_theme',
            '3_Column_Layout': '/themes/standard/Y3_a_3_Column_Layout_theme',
            '3_1_3_Column_Layout': '/themes/standard/Y3_b_3-1-3_Column_Layout_theme',
          };
          
          // Find theme path from theme name
          const themePath = themeMap[jsonData.theme] || '/themes/standard/Y0_Blank';
          
          // Set theme URL for BuilderJS6
          setThemeUrl(themePath);
          
          // Fetch the theme's index.json to get the correct internal theme name
          try {
            const themeIndexResponse = await fetch(`${themePath}/index.json`);
            if (themeIndexResponse.ok) {
              const themeIndexData = await themeIndexResponse.json();
              // Update the theme name in JSON to match what BuilderJS expects
              if (themeIndexData.name) {
                jsonData.theme = themeIndexData.name;
              }
            }
          } catch (themeErr) {
            console.warn('Could not fetch theme index.json:', themeErr);
            // Keep original theme name
          }
          
          // Set the corrected JSON data
          setInitialData(jsonData);
        } else {
          // No theme specified, use default
          setInitialData(jsonData);
        }
    } catch (error) {
      console.error('Error loading template:', error);
      
      // Better error handling - check for network errors
      let errorMessage = 'Failed to load template';
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response received from server. Please check your connection.';
      } else {
        // Something else happened
        errorMessage = error.message || 'Failed to load template';
      }
      
      toast.error(errorMessage);
      
      // Set empty template data to prevent infinite loading
      setInitialData({
        elementLists: [],
        formats: {},
        theme: 'Blank'
      });
      setThemeUrl('/themes/standard/Y0_Blank');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (jsonData, htmlData, templateName) => {
    try {
      const userId = config.getUserId();
      if (!userId) {
        toast.error('User not authenticated. Please login again.');
        return;
      }

      // Parse JSON if it's a string
      let jsonObject = jsonData;
      if (typeof jsonData === 'string') {
        try {
          jsonObject = JSON.parse(jsonData);
        } catch (e) {
          jsonObject = jsonData;
        }
      }

      // Validate jsonObject
      if (typeof jsonObject !== 'object' || jsonObject === null) {
        toast.error('Invalid template data. Please try again.');
        return;
      }

      const requestData = {
        userId,
        templateName: templateName || `BuilderJS6 Template ${new Date().toLocaleString()}`,
        html: htmlData || '',
        json: jsonObject,
      };

      if (id) {
        // Update existing template
        const response = await AxiosInstance.put(`/templates/${id}`, requestData);
        if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
          toast.success('Template updated successfully');
        } else {
          throw new Error(response.data?.message || 'Failed to update template');
        }
      } else {
        // Create new template
        const response = await AxiosInstance.post('/templates', requestData);
        if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
          toast.success('Template saved successfully');
          if (response.data?.data?.TemplateId) {
            navigate(`/builderjs6/${response.data.data.TemplateId}`);
          }
        } else {
          throw new Error(response.data?.message || 'Failed to save template');
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save template';
      toast.error(errorMessage);
    }
  };

  const handleExport = (html) => {
    // Create download link
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-template-${Date.now()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template exported successfully');
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading template...</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100vh', maxHeight: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <BuilderJS6
        initialData={initialData}
        onSave={handleSave}
        onExport={handleExport}
        themeUrl={themeUrl}
        viewMode={viewMode}
      />
    </div>
  );
};

export default BuilderJS6Page;

